import apiClient from './apiClient';

export interface SystemTask {
  queueType: string;
  queueName: string;
  taskId: string;
  status: string;
  createdAt?: string;
  startedAt?: string;
  finishedAt?: string;
  symbol?: string;
  bar?: string;
  error?: string;
  data?: any;
  result?: any;
}

export interface SystemTasksSummary {
  total: number;
  byQueueType: {
    'kline-drop'?: number;
    'kline-prompt': number;
    'kline-monitor-rules': number;
    'order': number;
    'coin-analysis': number;
    'price-monitor': number;
    'signal-backtest': number;
    'signal-optimize': number;
    'batch-backtest'?: number;
    'batch-optimize'?: number;
    'stock-collect'?: number;
    'stock-analyze'?: number;
  };
  byStatus?: {
    pending: number;
    running: number;
    completed: number;
    failed: number;
  };
}

export interface UserInfo {
  id: number;
  telegramId: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  role: string;
  isActive: boolean;
  isPremium: boolean;
  investedAmount: number | null;
  investedAt: string | null; // ISO date string from API
  totalProfit: number | null;
  createdAt: string; // ISO date string from API
  lastLoginAt: string | null; // ISO date string from API
}

export interface UserListResponse {
  success: boolean;
  data: {
    users: UserInfo[];
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface AutoTradingRecord {
  id: number;
  operationType: string;
  symbol: string;
  direction?: string | null;
  side?: string | null;
  posSide?: string | null;
  triggerPrice?: string | null;
  orderSize?: string | null;
  positionSize?: string | null;
  takeProfitRatio?: string | null;
  stopLossRatio?: string | null;
  status: string;
  okxOrdId?: string | null;
  okxClOrdId?: string | null;
  okxResponse?: any;
  errorMessage?: string | null;
  errorStack?: string | null;
  ctVal?: string | null;
  ctMult?: string | null;
  createdAt: string;
}

export interface HistoricalPosition {
  id: number;
  instId: string;
  instType: string;
  posId: string;
  posSide?: string | null;
  direction?: string | null;
  type?: string | null;
  realizedPnl?: string | null;
  pnl?: string | null;
  openAvgPx?: string | null;
  closeAvgPx?: string | null;
  openMaxPos?: string | null;
  closeTotalPos?: string | null;
  lever?: string | null;
  mgnMode?: string | null;
  fee?: string | null;
  fundingFee?: string | null;
  liqPenalty?: string | null;
  tag?: string | null;
  beijingDate: string;
  cTime?: string | null;
  uTime: string;
  createdAt: string;
}

export interface SystemPromptInfo {
  id: number;
  promptId: string; // 提示词ID（字符串格式，必填）
  title: string;
  content: string;
  description?: string | null;
  isActive: boolean;
  usageCount?: number; // 使用此提示词的监控任务数量
  createdAt: string;
  updatedAt: string;
}

export interface AIModelConfigInfo {
  id: number;
  name: string;
  apiKey: string;
  baseURL: string;
  model: string;
  priority: number;
  isActive: boolean;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

class AdminService {
  /**
   * 获取所有系统任务
   */
  async getSystemTasks(): Promise<{ tasks: SystemTask[]; summary: SystemTasksSummary }> {
    const response = await apiClient.get<{ tasks: SystemTask[]; summary: SystemTasksSummary }>('/admin/tasks');

    if (!response.success) {
      throw new Error(response.message || '获取系统任务失败');
    }

    return response.data;
  }

  /**
   * 删除系统任务
   */
  async deleteSystemTask(queueType: string, taskId: string): Promise<{ success: boolean; message?: string }> {
    const response = await apiClient.delete<{ success: boolean; message?: string }>(
      `/admin/tasks/${queueType}/${taskId}`,
    );

    if (!response.success) {
      throw new Error(response.message || '删除系统任务失败');
    }

    return response.data;
  }

  /**
   * 获取用户列表
   */
  async getUserList(
    page: number = 1,
    pageSize: number = 20,
    search?: string,
  ): Promise<{ users: UserInfo[]; pagination: { page: number; pageSize: number; total: number; totalPages: number } }> {
    const response = await apiClient.get<{
      users: UserInfo[];
      pagination: { page: number; pageSize: number; total: number; totalPages: number };
    }>('/admin/users', {
      params: { page, pageSize, search },
    });

    if (!response.success) {
      throw new Error(response.message || '获取用户列表失败');
    }

    // 接口返回格式: { success: true, data: { users: [...], pagination: {...} } }
    return response.data || { users: [], pagination: { page, pageSize, total: 0, totalPages: 0 } };
  }

  /**
   * 更新用户权限
   */
  async updateUserRole(userId: number, role: string): Promise<{ success: boolean; message?: string }> {
    const response = await apiClient.put<{ success: boolean; message?: string }>(`/admin/users/${userId}/role`, {
      role,
    });

    if (!response.success) {
      throw new Error(response.message || '更新用户权限失败');
    }

    return response.data;
  }

  /**
   * 更新用户投资信息
   */
  async updateUserInvestment(
    userId: number,
    investedAmount?: number | null,
    investedAt?: string | null,
  ): Promise<{ success: boolean; message?: string }> {
    const response = await apiClient.put<{ success: boolean; message?: string }>(`/admin/users/${userId}/investment`, {
      investedAmount,
      investedAt,
    });

    if (!response.success) {
      throw new Error(response.message || '更新用户投资信息失败');
    }

    return response.data;
  }

  /**
   * 获取自动交易记录
   */
  async getAutoTradingRecords(
    page: number = 1,
    pageSize: number = 20,
    operationType?: string,
    status?: string,
    symbol?: string,
  ): Promise<{
    records: AutoTradingRecord[];
    pagination: { page: number; pageSize: number; total: number; totalPages: number };
    summary: { total: number; success: number; failed: number; placeOrder: number; closePosition: number };
  }> {
    const response = await apiClient.get<{
      records: AutoTradingRecord[];
      pagination: { page: number; pageSize: number; total: number; totalPages: number };
      summary: { total: number; success: number; failed: number; placeOrder: number; closePosition: number };
    }>('/admin/auto-trading-records', {
      params: { page, pageSize, operationType, status, symbol },
    });

    if (!response.success) {
      throw new Error(response.message || '获取自动交易记录失败');
    }

    return (
      response.data || {
        records: [],
        pagination: { page, pageSize, total: 0, totalPages: 0 },
        summary: { total: 0, success: 0, failed: 0, placeOrder: 0, closePosition: 0 },
      }
    );
  }

  /**
   * 获取历史持仓记录
   */
  async getHistoricalPositions(
    page: number = 1,
    pageSize: number = 20,
    instId?: string,
    direction?: string,
    type?: string,
  ): Promise<{
    records: HistoricalPosition[];
    pagination: { page: number; pageSize: number; total: number; totalPages: number };
    summary: {
      total: number;
      totalPnL: number;
      totalRealizedPnl: number;
      longCount: number;
      shortCount: number;
      longPnL: number; // 做多总盈亏
      longRealizedPnl: number; // 做多已实现盈亏
      shortPnL: number; // 做空总盈亏
      shortRealizedPnl: number; // 做空已实现盈亏
      type2Count: number;
      type3Count: number;
    };
  }> {
    const response = await apiClient.get<{
      records: HistoricalPosition[];
      pagination: { page: number; pageSize: number; total: number; totalPages: number };
      summary: {
        total: number;
        totalPnL: number;
        totalRealizedPnl: number;
        longCount: number;
        shortCount: number;
        longPnL: number;
        longRealizedPnl: number;
        shortPnL: number;
        shortRealizedPnl: number;
        type2Count: number;
        type3Count: number;
      };
    }>('/account/historical-positions-paginated', {
      params: { page, pageSize, instId, direction, type },
    });

    if (!response.success) {
      throw new Error(response.message || '获取历史持仓记录失败');
    }

    return (
      response.data || {
        records: [],
        pagination: { page, pageSize, total: 0, totalPages: 0 },
        summary: {
          total: 0,
          totalPnL: 0,
          totalRealizedPnl: 0,
          longCount: 0,
          shortCount: 0,
          type2Count: 0,
          type3Count: 0,
        },
      }
    );
  }

  /**
   * 获取系统提示词列表
   */
  async getSystemPrompts(
    page: number = 1,
    pageSize: number = 20,
    search?: string,
    isActive?: boolean,
  ): Promise<{
    prompts: SystemPromptInfo[];
    total: number;
    page: number;
    pageSize: number;
  }> {
    const response = await apiClient.get<{
      prompts: SystemPromptInfo[];
      total: number;
      page: number;
      pageSize: number;
    }>('/admin/prompts', {
      params: { page, pageSize, search, isActive },
    });

    if (!response.success) {
      throw new Error(response.message || '获取提示词列表失败');
    }

    return response.data;
  }

  /**
   * 获取单个系统提示词
   */
  async getSystemPrompt(id: number): Promise<SystemPromptInfo> {
    const response = await apiClient.get<SystemPromptInfo>(`/admin/prompts/${id}`);

    if (!response.success) {
      throw new Error(response.message || '获取提示词失败');
    }

    return response.data;
  }

  /**
   * 创建系统提示词
   */
  async createSystemPrompt(data: {
    promptId: string;
    title: string;
    content: string;
    description?: string;
    isActive?: boolean;
  }): Promise<SystemPromptInfo> {
    const response = await apiClient.post<SystemPromptInfo>('/admin/prompts', data);

    if (!response.success) {
      throw new Error(response.message || '创建提示词失败');
    }

    return response.data;
  }

  /**
   * 更新系统提示词
   */
  async updateSystemPrompt(
    id: number,
    data: {
      promptId?: string; // 更新时可选，但如果提供则不能为空
      title?: string;
      content?: string;
      description?: string;
      isActive?: boolean;
    },
  ): Promise<SystemPromptInfo> {
    const response = await apiClient.put<SystemPromptInfo>(`/admin/prompts/${id}`, data);

    if (!response.success) {
      throw new Error(response.message || '更新提示词失败');
    }

    return response.data;
  }

  /**
   * 删除系统提示词
   */
  async deleteSystemPrompt(promptId: string): Promise<{ success: boolean; message?: string }> {
    const response = await apiClient.delete<{ success: boolean; message?: string }>(
      `/admin/prompts/${encodeURIComponent(promptId)}`,
    );

    if (!response.success) {
      throw new Error(response.message || '删除提示词失败');
    }

    return response.data;
  }

  /**
   * 生成内存快照
   */
  async generateHeapDump(): Promise<{
    success: boolean;
    message: string;
    data?: {
      filename: string;
      path: string;
      memoryUsage: {
        heapUsed: number;
        heapTotal: number;
        external: number;
        rss: number;
      };
    };
    error?: string;
  }> {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      data?: {
        filename: string;
        path: string;
        memoryUsage: {
          heapUsed: number;
          heapTotal: number;
          external: number;
          rss: number;
        };
      };
      error?: string;
    }>('/admin/heapdump');

    if (!response.success) {
      throw new Error(response.message || '生成内存快照失败');
    }

    return response.data;
  }

  /**
   * 获取AI模型配置列表
   */
  async getAIModelConfigs(
    page: number = 1,
    pageSize: number = 20,
    search?: string,
    isActive?: boolean,
  ): Promise<{
    configs: AIModelConfigInfo[];
    total: number;
    page: number;
    pageSize: number;
  }> {
    const response = await apiClient.get<{
      configs: AIModelConfigInfo[];
      total: number;
      page: number;
      pageSize: number;
    }>('/admin/ai-model-configs', {
      params: { page, pageSize, search, isActive },
    });

    if (!response.success) {
      throw new Error(response.message || '获取AI模型配置列表失败');
    }

    return response.data;
  }

  /**
   * 获取单个AI模型配置
   */
  async getAIModelConfig(id: number): Promise<AIModelConfigInfo> {
    const response = await apiClient.get<AIModelConfigInfo>(`/admin/ai-model-configs/${id}`);

    if (!response.success) {
      throw new Error(response.message || '获取AI模型配置失败');
    }

    return response.data;
  }

  /**
   * 创建AI模型配置
   */
  async createAIModelConfig(data: {
    name: string;
    apiKey: string;
    baseURL: string;
    model: string;
    priority?: number;
    isActive?: boolean;
    description?: string;
  }): Promise<AIModelConfigInfo> {
    const response = await apiClient.post<AIModelConfigInfo>('/admin/ai-model-configs', data);

    if (!response.success) {
      throw new Error(response.message || '创建AI模型配置失败');
    }

    return response.data;
  }

  /**
   * 更新AI模型配置
   */
  async updateAIModelConfig(
    id: number,
    data: {
      name?: string;
      apiKey?: string;
      baseURL?: string;
      model?: string;
      priority?: number;
      isActive?: boolean;
      description?: string;
    },
  ): Promise<AIModelConfigInfo> {
    const response = await apiClient.put<AIModelConfigInfo>(`/admin/ai-model-configs/${id}`, data);

    if (!response.success) {
      throw new Error(response.message || '更新AI模型配置失败');
    }

    return response.data;
  }

  /**
   * 删除AI模型配置
   */
  async deleteAIModelConfig(id: number): Promise<{ success: boolean; message?: string }> {
    const response = await apiClient.delete<{ success: boolean; message?: string }>(`/admin/ai-model-configs/${id}`);

    if (!response.success) {
      throw new Error(response.message || '删除AI模型配置失败');
    }

    return response.data;
  }

  /**
   * 测试AI模型配置
   */
  async testAIModelConfig(data: {
    configId: number;
    prompt: string;
    temperature?: number;
    maxTokens?: number;
    enableSearch?: boolean;
  }): Promise<{ taskId: string }> {
    const response = await apiClient.post<{ taskId: string }>('/admin/ai-model-configs/test', data);

    if (!response.success) {
      throw new Error(response.message || '测试AI模型配置失败');
    }

    return response.data;
  }

  /**
   * 获取测试任务状态
   */
  async getTestTaskStatus(taskId: string): Promise<SystemTask> {
    const response = await apiClient.get<SystemTask>(`/admin/ai-model-configs/test/status/${taskId}`);

    if (!response.success) {
      throw new Error(response.message || '获取测试任务状态失败');
    }

    return response.data;
  }
}

// 创建全局实例
export const adminService = new AdminService();
